import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AutoComplete,
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
} from 'primereact/autocomplete'
import { Column } from 'primereact/column'
import Button from '../../../components/ui/Button'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import { rowActionLabel } from '../../../lib/deleteLabel'
import { useToast } from '../../../components/ui/ToastProvider'
import { getUsers } from '../api/usersApi'
import { useGroupMembers } from '../hooks/useGroups'
import type { Group, GroupMember, UserSummary } from '../types'

const MIN_CHARS = 2

interface Props {
  group: Group
  /** Salt okunur kip (126, Kısıtlı Yetkili asistan): üye listesi okunur; arama/ekleme ve çıkarma çizilmez. */
  readOnly?: boolean
  adding: boolean
  removing: boolean
  onAdd: (userId: number, onDone: () => void) => void
  onRemove: (member: GroupMember) => void
}

/** Öneri seçeneği — `label` AutoComplete'in seçili kişiyi kutuda yazması içindir. */
type UserOption = UserSummary & { label: string }

const fullNameOf = (u: UserSummary) => [u.name, u.surname].filter(Boolean).join(' ') || u.userName || `#${u.id}`
const toOption = (u: UserSummary): UserOption => ({
  ...u,
  label: u.userName ? `${fullNameOf(u)} (${u.userName})` : fullNameOf(u),
})

/**
 * Bir grubun üyeleri (122) — Grup Yönetimi'nde satır açılınca görünür: üye tablosu, kullanıcı arayıp
 * ekleme ve gruptan çıkarma. Arama Kullanıcı Yönetimi'nin kendi ucundan yapılır (aynı yetki); zaten
 * üye olan kişiler öneri listesinde gösterilmez.
 */
export default function GroupMembersPanel({
  group,
  readOnly = false,
  adding,
  removing,
  onAdd,
  onRemove,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const groupName = i18n.language.startsWith('en') && group.nameEn ? group.nameEn : group.nameTr
  const toast = useToast()
  const members = useGroupMembers(group.id)
  const rows = members.data ?? []
  const memberIds = new Set(rows.map((m) => m.userId))

  const [query, setQuery] = useState<string | UserOption>('')
  const [selected, setSelected] = useState<UserOption | null>(null)
  const [suggestions, setSuggestions] = useState<UserOption[]>([])
  const seq = useRef(0)

  const complete = (e: AutoCompleteCompleteEvent) => {
    const q = e.query.trim()
    if (q.length < MIN_CHARS) {
      setSuggestions([])
      return
    }
    const mine = ++seq.current
    getUsers(q)
      .then((page) => {
        if (mine === seq.current)
          setSuggestions(page.content.filter((u) => !memberIds.has(u.id)).map(toOption))
      })
      .catch(() => {
        if (mine === seq.current) setSuggestions([])
      })
  }

  const onChange = (e: AutoCompleteChangeEvent) => {
    setQuery(e.value)
    setSelected(typeof e.value === 'object' && e.value ? (e.value as UserOption) : null)
  }

  const add = () => {
    if (!selected) return
    onAdd(selected.id, () => {
      toast.show({ severity: 'success', summary: t('admin.groups.members.added'), life: 3000 })
      setQuery('')
      setSelected(null)
    })
  }

  const confirmRemove = (m: GroupMember) =>
    confirmDialog({
      header: t('admin.groups.members.removeTitle'),
      message: t('admin.groups.members.removeConfirm', { name: m.fullName, group: groupName }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.groups.members.remove'),
      accept: () => onRemove(m),
    })

  return (
    <div className="admin-group-members">
      <div className="admin-group-members-head">
        <h3 className="admin-group-members-title">{t('admin.groups.members.title')}</h3>
        {!readOnly && (
          <div className="admin-group-members-add">
            <AutoComplete
              value={query}
              suggestions={suggestions}
              completeMethod={complete}
              field="label"
              delay={300}
              minLength={MIN_CHARS}
              placeholder={t('admin.groups.members.searchPlaceholder')}
              aria-label={t('admin.groups.members.searchPlaceholder')}
              onChange={onChange}
              panelClassName="admin-group-member-panel"
              itemTemplate={(u: UserOption) => (
                <div className="admin-group-member-option">
                  <span>{fullNameOf(u)}</span>
                  {u.userName && <span className="admin-group-member-sub">{u.userName}</span>}
                </div>
              )}
            />
            <Button
              icon="pi pi-plus"
              label={t('admin.groups.members.add')}
              disabled={!selected || adding}
              loading={adding}
              onClick={add}
            />
          </div>
        )}
      </div>
      <DataTable
        value={rows}
        loading={members.isLoading}
        dataKey="userId"
        paginator={false}
        emptyMessage={<EmptyState title={t('admin.groups.members.empty')} />}
      >
        <Column field="fullName" header={t('admin.groups.members.columns.fullName')} />
        <Column
          field="userName"
          header={t('admin.groups.members.columns.userName')}
          body={(m: GroupMember) => m.userName ?? ''}
        />
        <Column
          field="email"
          header={t('admin.groups.members.columns.email')}
          body={(m: GroupMember) => m.email ?? ''}
        />
        {!readOnly && (
          <Column
            header=""
            style={{ width: '4rem' }}
            body={(m: GroupMember) => (
              <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                disabled={removing}
                /* Erişilebilir ad KAYDI da söyler (THY AI review, 26.10.2026 — ikinci tur). Projenin kendi
   yardımcısı `rowActionLabel` kullanılır: ad boşsa adsız anahtara düşer. */
                aria-label={rowActionLabel(t, 'admin.groups.members.remove', m.fullName, { group: groupName })}
                tooltip={t('admin.groups.members.remove')}
                // Tablonun en sağındaki kolon: sağa açılan tooltip ekran dışına taşardı (üst tablodaki
                // kalem butonuyla aynı düzeltme).
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => confirmRemove(m)}
              />
            )}
          />
        )}
      </DataTable>
    </div>
  )
}
